import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  useMeQuery,
  useGetKadryRequestHistoryQuery,
  useGetKadryRequestQuery,
  useListKadryProfilesQuery,
  useListKadryWorkSitesQuery,
  usePatchKadryRequestMutation,
} from '@shared/api';
import {
  kadryDate,
  kadryDateTime,
  kadryErrorCode,
  kadryMoney,
  kadryRequestJustAdded,
  kadryRequestTransitions,
  kadryTimezoneLabel,
  useKadryAccess,
  useKadryStaff,
  type KadryPatchRequest,
  type KadryRequestTransition,
} from '@entities/kadry';
import {
  KadryActions,
  KadryBackLink,
  KadryBannerGap,
  KadryCardHead,
  KadryErrorBanner,
  KadryMetaList,
  KadryMuted,
  KadryPerson,
  KadryReasonDialog,
  KadryRequestStatusBadge,
  KadrySplit,
  KadryStack,
  KadryTitle,
} from '../ui';
import { AssigneesPanel } from './AssigneesPanel';
import { CandidatesPanel } from './CandidatesPanel';
import { HistoryPanel } from './HistoryPanel';
import { ProfilePanel } from './ProfilePanel';
import { RequestFormModal } from './RequestFormModal';
import { ScheduleDailyNote } from './ScheduleCost';

const Head = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const RawText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.6;
`;

const CandidatesGap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Hint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/* Ссылка-действие внутри значения параметра. */
const InlineEdit = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/** Карточка заявки: статус и переходы, текст, параметры, назначенные, история, профиль. */
export function RequestCard() {
  const { id = '' } = useParams();
  const { can, canWrite } = useKadryAccess();
  const staff = useKadryStaff();
  const me = useMeQuery().data?.user.id;
  const { data: r, isLoading, error, refetch } = useGetKadryRequestQuery(id, { skip: !id });
  const { refetch: refetchHistory } = useGetKadryRequestHistoryQuery(id, { skip: !id });
  const { refetch: refetchProfiles } = useListKadryProfilesQuery(id, { skip: !id });
  const { data: sites = [] } = useListKadryWorkSitesQuery({ include_inactive: true });
  const [patch, patchState] = usePatchKadryRequestMutation();
  // Переход запоминается вместе с версией заявки на момент нажатия: If-Match
  // уходит с ней, и если заявку успели изменить — сервер ответит конфликтом.
  const [transition, setTransition] = useState<{
    t: KadryRequestTransition;
    version: number;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const refreshAll = useCallback(() => {
    patchState.reset();
    refetch();
    refetchHistory();
    refetchProfiles();
  }, [patchState, refetch, refetchHistory, refetchProfiles]);

  if (isLoading) {
    return (
      <>
        <KadryBackLink to="/agents/kadry/requests">← Все заявки</KadryBackLink>
        <Head>
          <SkeletonLine $width="45%" $height={22} />
          <SkeletonLine $width="70%" />
          <SkeletonLine $width="30%" />
        </Head>
      </>
    );
  }
  if (error || !r) {
    return (
      <>
        <KadryBackLink to="/agents/kadry/requests">← Все заявки</KadryBackLink>
        {kadryErrorCode(error) === 'not_found' ? (
          <FallbackBanner tone="info" title="Заявка не найдена">
            Заявки нет или она вне вашей области доступа.
          </FallbackBanner>
        ) : (
          <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить заявку" />
        )}
      </>
    );
  }

  const full = r.access === 'full';
  // Заявка из вакансии hh.ru, ещё не запускалась (Р22).
  const justAdded = kadryRequestJustAdded(r);
  // Изменения — только с правом записи на агента (не «только чтение»).
  const canManage = canWrite('request_manage') && r.access !== 'none';
  // Заказчик (безопасный доступ) правит и отменяет только свой черновик.
  const canEdit = canManage && r.status !== 'closed' && (full || r.status === 'draft');
  const transitions = canManage
    ? kadryRequestTransitions(r.status, justAdded).filter(
        (t) => full || (r.status === 'draft' && t.to === 'closed'),
      )
    : [];
  const site = r.work_site_id ? sites.find((s) => s.id === r.work_site_id) : undefined;
  // Текст отдаётся при полном доступе и автору/заказчику; остальным — скрыт.
  const isAuthor = me != null && (r.created_by === me || r.requester_id === me);

  async function applyPatch(p: KadryPatchRequest, version: number): Promise<boolean> {
    if (!r) return false;
    try {
      await patch({ id: r.id, version, patch: p }).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async function onTransition(reason: string) {
    if (!transition) return;
    const ok = await applyPatch(
      { status: transition.t.to, reason: reason || null },
      transition.version,
    );
    if (ok) setTransition(null);
  }

  async function onEdit(p: KadryPatchRequest, baseVersion: number) {
    const ok = await applyPatch(p, baseVersion);
    if (ok) setEditOpen(false);
  }

  // После конфликта версий: перечитать заявку и закрыть открытое окно —
  // следующая попытка начнётся со свежего снимка.
  function refreshAndClose() {
    refreshAll();
    setTransition(null);
    setEditOpen(false);
  }

  const blockedActivation = (t: KadryRequestTransition) =>
    Boolean(t.needsApprovedProfile) && r.active_profile_version == null;

  return (
    <>
      <KadryBackLink to="/agents/kadry/requests">← Все заявки</KadryBackLink>

      <Head>
        <TitleRow>
          <KadryTitle>{r.title}</KadryTitle>
          <KadryRequestStatusBadge status={r.status} justAdded={justAdded} />
        </TitleRow>
        <Facts>
          <span>Объект: {site?.name ?? '—'}</span>
          <span>Подразделение: {r.department ?? '—'}</span>
          <span>Нужно: {r.headcount} чел.</span>
          {r.origin === 'hh_import' ? (
            <span>Создана {kadryDate(r.created_at)} из вакансии hh.ru</span>
          ) : (
            <span>
              Создана {kadryDate(r.created_at)}, <KadryPerson id={r.created_by} />
            </span>
          )}
          {r.access === 'safe' && <span>Вам доступно безопасное представление</span>}
        </Facts>
        {(transitions.length > 0 || canEdit) && (
          <KadryActions>
            {transitions.map((t) => (
              <Button
                key={t.to}
                type="button"
                $size="sm"
                $variant={t.variant}
                disabled={patchState.isLoading || blockedActivation(t)}
                title={blockedActivation(t) ? 'Сначала утвердите профиль поиска' : undefined}
                onClick={() => {
                  patchState.reset();
                  setTransition({ t, version: r.version });
                }}
              >
                {t.label}
              </Button>
            ))}
            {canEdit && (
              <Button
                type="button"
                $size="sm"
                $variant="ghost"
                onClick={() => {
                  patchState.reset();
                  setEditOpen(true);
                }}
              >
                Изменить заявку
              </Button>
            )}
          </KadryActions>
        )}
        {justAdded && full && (
          <Hint>
            <KadryMuted>
              Заявка создана автоматически из вакансии hh.ru и стоит на паузе: поиск и отклики по
              ней не идут. Проверьте карточку поиска ниже — должность, требования, объект, —
              утвердите её и нажмите «Запустить в работу».
            </KadryMuted>
          </Hint>
        )}
        {r.status === 'draft' && full && r.active_profile_version == null && (
          <Hint>
            <KadryMuted>
              Чтобы запустить заявку в работу, составьте и утвердите профиль поиска ниже.
            </KadryMuted>
          </Hint>
        )}
      </Head>

      {patchState.error && !transition && !editOpen ? (
        <KadryBannerGap>
          <KadryErrorBanner
            error={patchState.error}
            onRefresh={refreshAll}
            onDismiss={() => patchState.reset()}
          />
        </KadryBannerGap>
      ) : null}

      {r.active_profile_version != null && (
        <CandidatesGap>
          <CandidatesPanel request={r} />
        </CandidatesGap>
      )}

      <KadrySplit>
        <KadryStack>
          <Card>
            <KadryCardHead>
              <CardTitle>Текст заявки</CardTitle>
              {canEdit && (full || isAuthor) && (
                <Button
                  type="button"
                  $size="sm"
                  $variant="secondary"
                  onClick={() => {
                    patchState.reset();
                    setEditOpen(true);
                  }}
                >
                  Изменить текст
                </Button>
              )}
            </KadryCardHead>
            {r.raw_text ? (
              <RawText>{r.raw_text}</RawText>
            ) : full || isAuthor ? (
              <KadryMuted>Текст заявки не указан.</KadryMuted>
            ) : (
              <KadryMuted>Свободный текст заявки вашей роли не показывается.</KadryMuted>
            )}
          </Card>
          <ProfilePanel request={r} workSites={sites} onRefreshRequest={refreshAll} />
        </KadryStack>

        <KadryStack>
          <Card>
            <KadryCardHead>
              <CardTitle>Параметры</CardTitle>
            </KadryCardHead>
            <KadryMetaList>
              <dt>Объект</dt>
              <dd>
                {site ? (
                  <>
                    {site.name}
                    <br />
                    <KadryMuted as="span">{kadryTimezoneLabel(site.timezone)}</KadryMuted>
                  </>
                ) : (
                  '—'
                )}
              </dd>
              <dt>Численность</dt>
              <dd>{r.headcount}</dd>
              {r.budget_rub != null && (
                <>
                  <dt>Бюджет</dt>
                  <dd>{kadryMoney(r.budget_rub)}</dd>
                </>
              )}
              <dt>Повтор поиска</dt>
              <dd>
                каждые {r.schedule_hours} ч
                {canEdit && full && (
                  <>
                    {' '}
                    <InlineEdit
                      type="button"
                      onClick={() => {
                        patchState.reset();
                        setEditOpen(true);
                      }}
                    >
                      изменить
                    </InlineEdit>
                  </>
                )}
                <br />
                <ScheduleDailyNote hours={r.schedule_hours} />
              </dd>
              <dt>Заказчик</dt>
              <dd>
                <KadryPerson id={r.requester_id} />
              </dd>
              <dt>Профиль</dt>
              <dd>
                {r.active_profile_version != null
                  ? `версия ${r.active_profile_version}`
                  : 'не утверждён'}
              </dd>
              {r.activated_at && (
                <>
                  <dt>В работе с</dt>
                  <dd>{kadryDateTime(r.activated_at)}</dd>
                </>
              )}
              {r.closed_at && (
                <>
                  <dt>Закрыта</dt>
                  <dd>{kadryDateTime(r.closed_at)}</dd>
                </>
              )}
              <dt>Изменена</dt>
              <dd>{kadryDateTime(r.updated_at)}</dd>
            </KadryMetaList>
          </Card>
          <AssigneesPanel
            request={r}
            staff={staff}
            canManage={canManage && full}
            onRefresh={refreshAll}
          />
          <HistoryPanel requestId={r.id} />
        </KadryStack>
      </KadrySplit>

      <KadryReasonDialog
        open={transition != null}
        title={transition?.t.label ?? ''}
        description={transitionDescription(transition?.t ?? null, r.active_profile_version ?? null)}
        warning={
          transition?.t.terminal
            ? 'Закрытая заявка не возвращается в работу. Для нового подбора создайте новую заявку.'
            : undefined
        }
        confirmLabel={transition?.t.label ?? 'Подтвердить'}
        confirmVariant={transition?.t.variant === 'danger' ? 'danger' : 'primary'}
        reasonPlaceholder="Попадёт в историю статусов"
        busy={patchState.isLoading}
        errorSlot={
          patchState.error ? (
            <KadryErrorBanner error={patchState.error} onRefresh={refreshAndClose} />
          ) : undefined
        }
        onConfirm={onTransition}
        onCancel={() => {
          setTransition(null);
          patchState.reset();
        }}
      />

      <RequestFormModal
        open={editOpen}
        request={r}
        workSites={sites}
        showBudget={can('admin_settings')}
        fullAccess={full}
        requesterChoice={
          full && staff.available
            ? { options: staff.requesters, loading: staff.isLoading, nameOf: staff.nameOf }
            : null
        }
        busy={patchState.isLoading}
        error={patchState.error}
        onDismissError={() => patchState.reset()}
        onRefresh={refreshAndClose}
        onPatch={onEdit}
        onClose={() => {
          setEditOpen(false);
          patchState.reset();
        }}
      />
    </>
  );
}

function transitionDescription(
  t: KadryRequestTransition | null,
  activeVersion: number | null,
): string | undefined {
  if (!t) return undefined;
  switch (t.to) {
    case 'active':
      return `Заявка перейдёт в работу: поиск кандидатов пойдёт по утверждённой версии профиля${
        activeVersion != null ? ` ${activeVersion}` : ''
      } и будет повторяться по расписанию.`;
    case 'paused':
      return 'Повторный поиск и новые действия по заявке остановятся до возобновления.';
    case 'closed':
      return 'Заявка будет закрыта.';
    default:
      return undefined;
  }
}
